/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { AssistElement } from '../../../types/model';
import { SolverContext } from './SolverContextProvider';
import { CursorPopover } from '../popover/CursorPopover';
import { HoveredPopover } from '../popover/HoveredPopover';

type PopoverType = {
    lineId: string;
    partIndex: number;
    element: HTMLElement;
    assist: AssistElement;
    active?: boolean;
};

type PopoverContextType = {
    placePopover(popover: PopoverType): void;
    removePopovers(lineId: string): void;
};

export const PopoverContext = createContext<PopoverContextType>({
    placePopover() {},
    removePopovers() {},
});

export const PopoverContextProvider: FC<PropsWithChildren<{ available: boolean }>> = ({ available, children }) => {
    const {
        config: { popoverStrategy },
    } = useContext(SolverContext);
    const [popovers, setPopovers] = useState<PopoverType[]>([]);

    const placePopover = useCallback((popover: PopoverType) => popover.assist.message && setPopovers((prevState) => prevState.concat(popover)), []);
    const removePopovers = useCallback((lineId: string) => setPopovers((prevState) => prevState.filter((p) => p.lineId !== lineId)), []);

    const context: PopoverContextType = useMemo(() => ({ placePopover, removePopovers }), [placePopover, removePopovers]);

    return (
        <PopoverContext.Provider value={context}>
            {children}
            {available &&
                popovers.map((p, index) =>
                    popoverStrategy === 'cursor' ? (
                        <CursorPopover key={index} element={p.element} assist={p.assist} active={p.active} />
                    ) : (
                        <HoveredPopover key={index} element={p.element} assist={p.assist} active={p.active} />
                    )
                )}
        </PopoverContext.Provider>
    );
};
